import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { findIana, findWindows } from 'windows-iana';
import { IIdName } from '@/interfaces/general/IIdName.ts';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Converts a sitewide timezone (usually IANA, sometimes Windows Id) into the
 * Windows DisplayName string Flextrack backends resolve via TimeZoneInfo.DisplayName.
 */
export function toFlextrackTimezoneDisplayName (
    timezoneId: string,
    timeZoneInfo: Array<IIdName>,
): string {
    const DEFAULT_FLEXTRACK_TIMEZONE_DISPLAY_NAME = '(UTC) Coordinated Universal Time';
    if (!timezoneId || !timeZoneInfo.length) return DEFAULT_FLEXTRACK_TIMEZONE_DISPLAY_NAME;

    const byDisplayName = timeZoneInfo.find(tz => tz.name === timezoneId);
    if (byDisplayName) return byDisplayName.name;

    const byWindowsId = timeZoneInfo.find(tz => tz.id === timezoneId);
    if (byWindowsId) return byWindowsId.name;

    const windowsIds = findWindows(timezoneId);
    const byMappedWindowsId = timeZoneInfo.find(tz => windowsIds.some(windowsId => windowsId === tz.id));
    if (byMappedWindowsId) return byMappedWindowsId.name;

    const byIanaAlias = timeZoneInfo.find(tz => findIana(tz.id).some(ianaName => ianaName === timezoneId));
    if (byIanaAlias) return byIanaAlias.name;

    return DEFAULT_FLEXTRACK_TIMEZONE_DISPLAY_NAME;
}

export function formatTimezoneOffsetDifference (
    selectedOffsetMinutes: number,
    referenceOffsetMinutes: number,
): string | null {
    const diffMinutes = selectedOffsetMinutes - referenceOffsetMinutes;

    if (diffMinutes === 0) return null;

    const sign = diffMinutes >= 0 ? '+' : '-';
    const absMinutes = Math.abs(diffMinutes);
    const hours = Math.floor(absMinutes / 60);
    const minutes = absMinutes % 60;

    return `${sign}${hours}:${String(minutes).padStart(2, '0')}`;
}

function getTimezoneAbbreviation (timezoneId: string, date: Date): string {
    const timezoneName = new Intl.DateTimeFormat('en-GB', {
        timeZone: timezoneId,
        timeZoneName: 'short',
    })
        .formatToParts(date)
        .find(part => part.type === 'timeZoneName')?.value;

    return timezoneName ?? timezoneId;
}

export function getTimezoneOffsetSuffix (timezoneId: string, referenceTimezoneId: string, date: Date = new Date()): string | null {
    return formatTimezoneOffsetDifference(
        dayjs(date).tz(timezoneId).utcOffset(),
        dayjs(date).tz(referenceTimezoneId).utcOffset(),
    );
}

export function getTimezoneOffsetLabel (timezoneId: string, referenceTimezoneId: string, date: Date = new Date()): string | null {
    const suffix = getTimezoneOffsetSuffix(timezoneId, referenceTimezoneId, date);
    if (!suffix) return null;

    const selectedTimezoneName = getTimezoneAbbreviation(timezoneId, date);
    const referenceTimezoneName = getTimezoneAbbreviation(referenceTimezoneId, date);

    return `${selectedTimezoneName} (${referenceTimezoneName} ${suffix})`;
}
